import React, { useState, useCallback } from 'react';
import { IUserJson } from '@scale/shared/types/user';
import { Typography, Button, TextArea, Spinner } from '@scale/scaleui';
import API from '@scale/frontend-lib/dashboard-api';
import { parseAnythingSeparatedIds } from '@scale/shared/utils';

interface TaskerScreeningPurgeScreeningsProps {
  user: IUserJson;
}

const TaskerScreeningPurgeScreenings: React.FC<TaskerScreeningPurgeScreeningsProps> = () => {
  const [screeningIdsString, setScreeningIdsString] = useState<string>('');
  const [loadingPurge, setLoadingPurge] = useState<boolean>(false);
  const [infoMessage, setInfoMessage] = useState<string>('');

  const [sbqIdsString, setSbqIdsString] = useState<string>('');
  const [loadingSbq, setLoadingSbq] = useState<boolean>(false);
  const [sbqInfoMessage, setSbqInfoMessage] = useState<string>('');

  const purgeScreenings = async (screeningIds: string[]) => {
    return await API.genai.purgeScreenings({
      screeningIds,
    });
  };

  const handlePurgeScreenings = useCallback(async () => {
    const screeningIds = parseAnythingSeparatedIds(screeningIdsString);
    setLoadingPurge(true);
    const response = await purgeScreenings(screeningIds);
    setInfoMessage(response.message);
    setLoadingPurge(false);
  }, [screeningIdsString]);

  const sbqScreenings = async (sbqScreeningIds: string[]) => {
    return await API.genai.sbqScreenings({
      screeningIds: sbqScreeningIds,
    });
  };

  const handleSBQScreenings = useCallback(async () => {
    setLoadingSbq(true);
    const parsedSbqIds = parseAnythingSeparatedIds(sbqIdsString);
    const response = await sbqScreenings(parsedSbqIds);
    setSbqInfoMessage(response.message);
    setLoadingSbq(false);
  }, [sbqIdsString]);

  return (
    <div className="scaleui scale-theme scale-tailwind p-4">
      <div>
        <Typography.H4>Purge Pending (not yet attempted) Screenings</Typography.H4>
        <Typography.Paragraph2>
          Use this tool to purge pending screening attempts that will no longer need to be audited.
          Input a list of object IDs, anything separated. These object IDs should correspond to
          entries in ScreeningReviewQualificationSection table. Purging will set the status of these
          screening attempts to <span className="font-bold">"paused"</span>, and will prevent them
          from appearing in the Tasker Screening Grading table.
        </Typography.Paragraph2>
        <TextArea
          className="mt-4"
          id="purge"
          value={screeningIdsString}
          onChange={e => setScreeningIdsString(e.target.value)}
          placeholder="Screening IDs"
        />
        <Button
          className="mt-4 mb-4"
          onClick={handlePurgeScreenings}
          disabled={!screeningIdsString?.length || loadingPurge}
        >
          Purge Screening Attempts
        </Button>
        {loadingPurge && <Spinner size="md" />}
        <p>{infoMessage}</p>
      </div>

      <div>
        <Typography.H4>Send Back to Queue Audited Screenings</Typography.H4>
        <div className="flex flex-col gap-1">
          <Typography.Paragraph2>
            Use this tool to purge screening attempts and their attached scores. Meant to be used to
            clear screenings from bad actors/spammers.
          </Typography.Paragraph2>
          <Typography.Paragraph2>
            Input a list of ScreeningReviewQualificationSection ids. The purging process will set
            the status of the screening attempts to <span className="font-bold">"pending"</span>,
            which will return them to the queue. The scores associated with the screening attempts
            will be removed, and the averages recalculated.
          </Typography.Paragraph2>
        </div>
        <TextArea
          className="mt-4"
          id="sbq"
          value={sbqIdsString}
          onChange={e => setSbqIdsString(e.target.value)}
          placeholder="Screening IDs"
        />
        <Button
          className="mt-4 mb-4"
          onClick={handleSBQScreenings}
          disabled={!sbqIdsString?.length || loadingSbq}
        >
          Purge Screening Attempts
        </Button>
        {loadingSbq && <Spinner size="md" />}
        <p>{sbqInfoMessage}</p>
      </div>
    </div>
  );
};

export default TaskerScreeningPurgeScreenings;
